import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  AreaChart, Area,
  BarChart, Bar,
  PieChart, Pie, Cell, Legend,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { Users, Activity, DollarSign, Zap } from 'lucide-react';

import StatCard from './components/StatCard';
import ChartCard from './components/ChartCard';
import ApiHitsSection from './components/ApiHitsSection';
import { downloadChartPNG, DownloadBtn } from './components/downloadUtil';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

const EMPTY_SUPER_STATS = {
  totalUsers: { label: 'Total Registered Users', value: 0, change: 0 },
  mau: { label: 'Monthly Active Users', value: 0, change: 0 },
  mrr: { label: 'MRR (USD)', value: 0, change: 0 },
  queriesThisMonth: { label: 'Queries This Month', value: 0, change: 0 },
};

// ── Custom Tooltip ───────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label, isDark, isCurrency = false }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-xl border text-xs px-3 py-2 shadow-lg"
      style={{
        background: isDark ? '#0f1729' : '#fff',
        borderColor: isDark ? '#1e2d52' : '#e2e8f0',
        color: isDark ? '#c9d1d9' : '#374151',
      }}
    >
      {label && <p className="font-semibold mb-1">{label}</p>}
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {p.name}: {isCurrency ? '$' : ''}{typeof p.value === 'number' ? p.value.toLocaleString() : p.value}
        </p>
      ))}
    </div>
  );
};

const SuperAdminDashboard = ({ isDark, token }) => {
  const textMuted = isDark ? '#8b949e' : '#64748b';
  const gridColor = isDark ? 'rgba(100,160,255,0.07)' : '#f1f5f9';
  const axisColor = isDark ? '#3d4f7a' : '#cbd5e1';
  // ── Chart refs ──
  const userGrowthRef = useRef(null);
  const planDistRef = useRef(null);
  const topAgentsRef = useRef(null);
  const categoryPieRef = useRef(null);
  const creditsRef = useRef(null);

  // Live API state (no mock fallback)
  const [superAdminStats, setSuperAdminStats] = useState(EMPTY_SUPER_STATS);
  const [userGrowthData, setUserGrowthData] = useState([]);
  const [planDistributionData, setPlanDistributionData] = useState([]);
  const [topAgentsByUsage, setTopAgentsByUsage] = useState([]);
  const [agentUsageByCategoryData, setAgentUsageByCategoryData] = useState([]);
  const [creditsData, setCreditsData] = useState([]);
  const [apiHitsData, setApiHitsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // ── Single loading-key state ──
  const [dlKey, setDlKey] = useState('');
  const dl = (ref, key, file) => downloadChartPNG(ref, file, isDark, (v) => setDlKey(v ? key : ''));

  const [accessRequests, setAccessRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedRole, setSelectedRole] = useState('user');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const [inviteHistory, setInviteHistory] = useState([]);

  const fetchInviteHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/invite-history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInviteHistory(Array.isArray(data) ? data : []);
    } catch {}
  }, [token]);

  useEffect(() => { fetchInviteHistory(); }, [fetchInviteHistory]);

  const fetchAccessRequests = useCallback(async () => {
    setRequestsLoading(true);
    try {
      const { data } = await axios.get(`${API}/access-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccessRequests(Array.isArray(data) ? data : []);
    } catch {
      // non-critical, don't block dashboard
    } finally {
      setRequestsLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAccessRequests(); }, [fetchAccessRequests]);

  const handleInvite = async () => {
    if (!selectedRequest) return;
    setInviteLoading(true);
    try {
      await axios.post(`${API}/invite`, {
        email: selectedRequest.email,
        role: selectedRole,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Invite sent to ${selectedRequest.email}`);
      setShowInviteModal(false);
      setSelectedRequest(null);
      fetchAccessRequests();
      fetchInviteHistory();      
    } finally {
      setInviteLoading(false);
    }
  };

  const handleDeny = async (email) => {
    try {
      await axios.post(`${API}/deny/${email}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Request denied');
      setAccessRequests(prev => prev.filter(r => r.email !== email));
      fetchAccessRequests();
    } catch {
      toast.error('Failed to deny request');
    }
  };

  const fetchAll = useCallback(async () => {
    if (!token) {
      setError('Missing authentication token. Please sign in again.');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    const headers = { Authorization: `Bearer ${token}` };
    const calls = [
      ['stats', axios.get(`${API}/super/stats`, { headers })],
      ['growth', axios.get(`${API}/super/user-growth`, { headers })],
      ['plan', axios.get(`${API}/super/plan-distribution`, { headers })],
      ['top', axios.get(`${API}/super/top-agents`, { headers })],
      ['category', axios.get(`${API}/super/category-usage`, { headers })],
      ['credits', axios.get(`${API}/super/credits-flow`, { headers })],
      ['hits', axios.get(`${API}/super/api-hits`, { headers })],
    ];

    const results = await Promise.allSettled(calls.map(([, req]) => req));
    const failed = [];

    results.forEach((result, index) => {
      const key = calls[index][0];
      if (result.status === 'rejected') {
        failed.push(result.reason);
        return;
      }

      const data = result.value?.data;
      if (key === 'stats') setSuperAdminStats(data || EMPTY_SUPER_STATS);
      if (key === 'growth') setUserGrowthData(Array.isArray(data) ? data : []);
      if (key === 'plan') setPlanDistributionData(Array.isArray(data) ? data : []);
      if (key === 'top') setTopAgentsByUsage(Array.isArray(data) ? data : []);
      if (key === 'category') setAgentUsageByCategoryData(Array.isArray(data) ? data : []);
      if (key === 'credits') setCreditsData(Array.isArray(data) ? data : []);
      if (key === 'hits') setApiHitsData(data || null);
    });

    if (failed.length) {
      const status = failed[0]?.response?.status;
      if (status === 401) {
        setError('Session expired. Please sign in again.');
      } else if (status === 403) {
        setError('You are authenticated but do not have admin access.');
      } else {
        setError(`Failed to load ${failed.length} super admin data request(s).`);
      }
    }

    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const DevResetPanel = ({ isDark, token }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('');

  const handleReset = async () => {
    if (!email) return;
    setStatus('Resetting...');
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/invite/dev/reset-user/${encodeURIComponent(email)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStatus(` Reset done for ${email}`);
      setEmail('');
    } catch (err) {
      setStatus(` ${err.response?.data?.detail || err.message}`);
    }
  };

  return (
    <div style={{
      border: '1px dashed #F87217', borderRadius: 12,
      padding: 16, background: isDark ? 'rgba(248,114,23,0.05)' : '#fff7ed'
    }}>
      <p style={{ color: '#F87217', fontWeight: 700, fontSize: 12, marginBottom: 8 }}>
        🛠 DEV ONLY — Reset Invited User
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          value={email} onChange={e => setEmail(e.target.value)}
          placeholder="user@email.com"
          style={{
            flex: 1, padding: '6px 12px', borderRadius: 8, fontSize: 12,
            border: '1px solid #F87217',
            background: isDark ? '#0f1729' : '#fff',
            color: isDark ? '#f0f6fc' : '#0f172a'
          }}
        />
        <button onClick={handleReset} style={{
          padding: '6px 16px', borderRadius: 8, fontSize: 12,
          fontWeight: 700, color: '#fff', border: 'none', cursor: 'pointer',
          background: 'linear-gradient(135deg,#F87217,#FF9A4E)'
        }}>Reset</button>
      </div>
      {status && <p style={{ marginTop: 8, fontSize: 11, color: isDark ? '#94a3b8' : '#64748b' }}>{status}</p>}
    </div>
  );
};

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">

      {/* DEV ONLY reset panel */}
    {process.env.NODE_ENV === 'development' && (
      <DevResetPanel isDark={isDark} token={token} />
    )}

      {loading && (
        <div
          className="rounded-xl border px-3 py-2 text-xs"
          style={{
            background: isDark ? '#0f1729' : '#fff',
            borderColor: isDark ? '#1e2d52' : '#e2e8f0',
            color: isDark ? '#94a3b8' : '#475569',
          }}
        >
          Loading live super admin metrics...
        </div>
      )}
      {!!error && (
        <div
          className="rounded-xl border px-3 py-2 text-xs flex items-center justify-between gap-3"
          style={{
            background: isDark ? 'rgba(239,68,68,0.1)' : '#fef2f2',
            borderColor: isDark ? 'rgba(239,68,68,0.35)' : '#fecaca',
            color: isDark ? '#fca5a5' : '#991b1b',
          }}
        >
          <span>{error}</span>
          <button
            onClick={fetchAll}
            className="px-2 py-1 rounded-md text-[11px] font-semibold"
            style={{
              background: isDark ? 'rgba(248,114,23,0.2)' : '#ffedd5',
              color: '#F87217',
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* ── Access Requests ─────────────────────────────────────────────── */}
{(accessRequests.length > 0 || requestsLoading) && (
  <div
    className="rounded-2xl border overflow-hidden"
    style={{
      background: isDark ? '#0f1729' : '#fff',
      borderColor: isDark ? '#1e2d52' : '#e2e8f0',
    }}
  >
    {/* Header */}
    <div
      className="flex items-center justify-between px-5 py-4 cursor-pointer"
      onClick={() => setCollapsed(c => !c)}
      style={{ borderBottom: collapsed ? 'none' : `1px solid ${isDark ? '#1e2d52' : '#e2e8f0'}` }}
    >
      <div className="flex items-center gap-3">
        <span className="text-sm font-bold" style={{ color: isDark ? '#f0f6fc' : '#0f172a' }}>
          Access Requests
        </span>
        {accessRequests.length > 0 && (
          <span
            className="text-xs font-bold px-2 py-0.5 rounded-full"
            style={{ background: 'rgba(248,114,23,0.15)', color: '#F87217' }}
          >
            {accessRequests.length} pending
          </span>
        )}
      </div>
      <span style={{ color: isDark ? '#64748b' : '#94a3b8', fontSize: 12 }}>
        {collapsed ? '▼ Show' : '▲ Hide'}
      </span>
    </div>

    {/* Table */}
    {!collapsed && (
      <div className="overflow-x-auto">
        {requestsLoading ? (
          <div className="py-8 text-center text-xs" style={{ color: isDark ? '#64748b' : '#94a3b8' }}>
            Loading requests...
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr style={{ background: isDark ? '#0a0f1e' : '#f8fafc' }}>
                {['Email', 'Requested At', 'Actions'].map(h => (
                  <th key={h} className="px-5 py-3 text-left font-semibold"
                    style={{ color: isDark ? '#64748b' : '#94a3b8' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {accessRequests.map((req, i) => (
                <tr
                  key={req.id}
                  style={{
                    borderTop: `1px solid ${isDark ? '#1e2d52' : '#f1f5f9'}`,
                    background: i % 2 === 0 ? 'transparent' : isDark ? 'rgba(255,255,255,0.01)' : 'rgba(0,0,0,0.01)',
                  }}
                >
                  <td className="px-5 py-3 font-medium" style={{ color: isDark ? '#c9d1d9' : '#1e293b' }}>
                    {req.email}
                  </td>
                  <td className="px-5 py-3" style={{ color: isDark ? '#64748b' : '#94a3b8' }}>
                    {new Date(req.created_at).toLocaleString()}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => { setSelectedRequest(req); setSelectedRole('user'); setShowInviteModal(true); }}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition-all"
                        style={{ background: 'linear-gradient(135deg,#F87217,#FF9A4E)', boxShadow: '0 2px 8px rgba(248,114,23,0.3)' }}
                      >
                        Invite
                      </button>
                      <button
                        onClick={() => handleDeny(req.email)}
                        className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                        style={{
                          background: isDark ? 'rgba(239,68,68,0.1)' : '#fef2f2',
                          color: '#ef4444',
                          border: '1px solid rgba(239,68,68,0.2)',
                        }}
                      >
                        Deny
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    )}
  </div>
)}

{/* ── Invite History ──────────────────────────────────────────────── */}
{inviteHistory.length > 0 && (
  <div className="rounded-2xl border overflow-hidden"
    style={{ background: isDark ? '#0f1729' : '#fff', borderColor: isDark ? '#1e2d52' : '#e2e8f0' }}>
    <div className="px-5 py-4" style={{ borderBottom: `1px solid ${isDark ? '#1e2d52' : '#e2e8f0'}` }}>
      <span className="text-sm font-bold" style={{ color: isDark ? '#f0f6fc' : '#0f172a' }}>
        Invite History
      </span>
    </div>
    <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: '260px' }}>
      <table className="w-full text-xs">
        <thead>
          <tr style={{ background: isDark ? '#0a0f1e' : '#f8fafc' }}>
            {['Email', 'Role', 'Status', 'Sent At', 'Used At'].map(h => (
              <th key={h} className="px-5 py-3 text-left font-semibold"
                style={{ color: isDark ? '#64748b' : '#94a3b8' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {inviteHistory.map((inv, i) => (
            <tr key={inv.id}
              style={{ borderTop: `1px solid ${isDark ? '#1e2d52' : '#f1f5f9'}` }}>
              <td className="px-5 py-3" style={{ color: isDark ? '#c9d1d9' : '#1e293b' }}>{inv.email}</td>
              <td className="px-5 py-3">
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold capitalize"
                  style={{ background: 'rgba(248,114,23,0.1)', color: '#F87217' }}>
                  {inv.assigned_role}
                </span>
              </td>
              <td className="px-5 py-3">
                <span className="px-2 py-0.5 rounded-full text-xs font-semibold capitalize"
                  style={{
                    background: inv.status === 'accepted' ? 'rgba(16,185,129,0.1)'
                      : inv.status === 'declined' ? 'rgba(239,68,68,0.1)'
                      : inv.status === 'sent' ? 'rgba(59,130,246,0.1)'
                      : 'rgba(100,116,139,0.1)',
                    color: inv.status === 'accepted' ? '#10b981'
                      : inv.status === 'declined' ? '#ef4444'
                      : inv.status === 'sent' ? '#3b82f6'
                      : '#64748b',
                  }}>
                  {inv.status}
                </span>
              </td>
              <td className="px-5 py-3" style={{ color: isDark ? '#64748b' : '#94a3b8' }}>
                {new Date(inv.created_at).toLocaleString()}
              </td>
              <td className="px-5 py-3" style={{ color: isDark ? '#64748b' : '#94a3b8' }}>
                {inv.used_at ? new Date(inv.used_at).toLocaleString() : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
)}

{/* ── Invite Modal ─────────────────────────────────────────────────── */}
{showInviteModal && selectedRequest && (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center"
    style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
    onClick={() => setShowInviteModal(false)}
  >
    <div
      className="rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl"
      style={{
        background: isDark ? '#0f1729' : '#fff',
        border: `1px solid ${isDark ? '#1e2d52' : '#e2e8f0'}`,
      }}
      onClick={e => e.stopPropagation()}
    >
      <h3 className="text-base font-bold mb-1" style={{ color: isDark ? '#f0f6fc' : '#0f172a' }}>
        Send Invite
      </h3>
      <p className="text-xs mb-5" style={{ color: isDark ? '#64748b' : '#94a3b8' }}>
        Inviting <strong style={{ color: '#F87217' }}>{selectedRequest.email}</strong>
      </p>

      <div className="space-y-3">
        <label className="block text-xs font-semibold mb-1" style={{ color: isDark ? '#c9d1d9' : '#334155' }}>
          Assign Role
        </label>
        <div className="flex gap-3">
          {['user', 'admin'].map(role => (
            <button
              key={role}
              onClick={() => setSelectedRole(role)}
              className="flex-1 py-2.5 rounded-xl text-xs font-semibold capitalize transition-all"
              style={{
                background: selectedRole === role
                  ? 'linear-gradient(135deg,#F87217,#FF9A4E)'
                  : isDark ? '#0a0f1e' : '#f8fafc',
                color: selectedRole === role ? '#fff' : isDark ? '#64748b' : '#94a3b8',
                border: selectedRole === role
                  ? '1px solid transparent'
                  : `1px solid ${isDark ? '#1e2d52' : '#e2e8f0'}`,
                boxShadow: selectedRole === role ? '0 4px 12px rgba(248,114,23,0.3)' : 'none',
              }}
            >
              {role}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-3 mt-6">
        <button
          onClick={() => setShowInviteModal(false)}
          className="flex-1 py-2.5 rounded-xl text-xs font-semibold"
          style={{
            background: 'transparent',
            border: `1px solid ${isDark ? '#1e2d52' : '#e2e8f0'}`,
            color: isDark ? '#64748b' : '#94a3b8',
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleInvite}
          disabled={inviteLoading}
          className="flex-1 py-2.5 rounded-xl text-xs font-semibold text-white transition-all"
          style={{
            background: inviteLoading ? '#f0a067' : 'linear-gradient(135deg,#F87217,#FF9A4E)',
            boxShadow: inviteLoading ? 'none' : '0 4px 12px rgba(248,114,23,0.35)',
            cursor: inviteLoading ? 'not-allowed' : 'pointer',
          }}
        >
          {inviteLoading ? 'Sending...' : 'Send Invite'}
        </button>
      </div>
    </div>
  </div>
)}

      {/* ── API Hits Analytics (TOP) ────────────────────────────────── */}
      <ApiHitsSection isDark={isDark} apiData={apiHitsData} />

      {/* ── Divider ─────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1, height: 1, background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)' }} />
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: isDark ? '#64748b' : '#94a3b8' }}>
          Platform Overview
        </span>
        <div style={{ flex: 1, height: 1, background: isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)' }} />
      </div>

      {/* ── KPI Stat Cards ──────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          label={superAdminStats.totalUsers?.label || 'Total Registered Users'}
          value={superAdminStats.totalUsers?.value ?? 0}
          unit="Users"
          change={superAdminStats.totalUsers?.change ?? 0}
          icon={Users}
          isDark={isDark}
        />
        <StatCard
          label={superAdminStats.mau?.label || 'Monthly Active Users'}
          value={superAdminStats.mau?.value ?? 0}
          unit="Users"
          change={superAdminStats.mau?.change ?? 0}
          icon={Activity}
          iconBg="bg-violet-100"
          iconColor="text-violet-600"
          isDark={isDark}
        />
        <StatCard
          label={superAdminStats.mrr?.label || 'MRR (USD)'}
          value={superAdminStats.mrr?.value ?? 0}
          change={superAdminStats.mrr?.change ?? 0}
          isCurrency
          icon={DollarSign}
          iconBg="bg-emerald-100"
          iconColor="text-emerald-600"
          isDark={isDark}
        />
        <StatCard
          label={superAdminStats.queriesThisMonth?.label || 'Queries This Month'}
          value={superAdminStats.queriesThisMonth?.value ?? 0}
          unit="Queries"
          change={superAdminStats.queriesThisMonth?.change ?? 0}
          icon={Zap}
          iconBg="bg-cyan-100"
          iconColor="text-cyan-600"
          isDark={isDark}
        />
      </div>

      {/* ── Row 1: User Growth + Plan Distribution ───────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">

        {/* User Growth Over Time – horizontal scroll */}
        <div ref={userGrowthRef} className="xl:col-span-2">
          <ChartCard
            title="User Growth Over Time"
            subtitle="Last 90 days · daily users"
            isDark={isDark}
            action={<DownloadBtn loading={dlKey === 'growth'} isDark={isDark} onClick={() => dl(userGrowthRef, 'growth', 'user-growth')} />}
          >
            <div style={{
              overflowX: 'auto', overflowY: 'hidden',
              scrollbarWidth: 'thin',
              scrollbarColor: isDark ? '#3d4f7a #0f1729' : '#cbd5e1 #f8fafc',
            }}>
              <div style={{ minWidth: Math.max(userGrowthData.length * 18, 500) }}>
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={userGrowthData} margin={{ top: 4, right: 16, bottom: 45, left: 10 }}>
                    <defs>
                      <linearGradient id="ugGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="10%" stopColor="#F87217" stopOpacity={0.25} />
                        <stop offset="90%" stopColor="#F87217" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke={gridColor} vertical={false} />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 9, fill: axisColor }}
                      tickLine={false} axisLine={false}
                      interval={6} angle={-35} textAnchor="end" height={55}
                    />
                    <YAxis tick={{ fontSize: 10, fill: axisColor }} tickLine={false} axisLine={false} width={48} domain={[0, dataMax => Math.max(dataMax, 200)]} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                    <Tooltip content={<CustomTooltip isDark={isDark} />} />
                    <Area type="monotone" dataKey="users" name="Users" stroke="#F87217" strokeWidth={2.5} fill="url(#ugGrad)" dot={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px 0', fontSize: 10, color: textMuted, fontWeight: 500 }}>
              <span>Y-Axis: Users</span>
              <span>X-Axis: Date</span>
            </div>
          </ChartCard>
        </div>

        {/* Plan Distribution Donut */}
        <div ref={planDistRef}>
          <ChartCard
            title="Plan Distribution"
            subtitle="Current subscribers"
            isDark={isDark}
            action={<DownloadBtn loading={dlKey === 'plan'} isDark={isDark} onClick={() => dl(planDistRef, 'plan', 'plan-distribution')} />}
          >
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={planDistributionData}
                  cx="50%" cy="50%"
                  innerRadius={50} outerRadius={75}
                  paddingAngle={3} dataKey="value"
                  label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                >
                  {planDistributionData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} stroke="transparent" />
                  ))}
                </Pie>
                <Legend
                  iconType="circle" iconSize={8}
                  formatter={(v) => <span style={{ fontSize: 11, color: isDark ? '#94a3b8' : '#64748b' }}>{v}</span>}
                />
                <Tooltip
                  formatter={(value, name) => [value.toLocaleString(), name]}
                  contentStyle={{ background: isDark ? '#0f1729' : '#fff', border: `1px solid ${isDark ? '#1e2d52' : '#e2e8f0'}`, borderRadius: 12, fontSize: 12, color: isDark ? '#c9d1d9' : '#374151' }}
                  itemStyle={{ color: isDark ? '#c9d1d9' : '#374151' }}
                  labelStyle={{ color: isDark ? '#f0f6fc' : '#0f172a', fontWeight: 600 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>

      {/* ── Row 2: Top Agents + Category Pie ────────────────────────── */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">

        {/* Top 10 Agents Horizontal Bar */}
        <div ref={topAgentsRef} className="xl:col-span-2">
          <ChartCard
            title="Top 10 Agents by Usage"
            subtitle="Total queries this month"
            isDark={isDark}
            action={<DownloadBtn loading={dlKey === 'topagents'} isDark={isDark} onClick={() => dl(topAgentsRef, 'topagents', 'top-agents-usage')} />}
          >
            <ResponsiveContainer width="100%" height={280}>
              <BarChart layout="vertical" data={topAgentsByUsage} margin={{ top: 0, right: 16, bottom: 0, left: 0 }}>
                <CartesianGrid stroke={gridColor} horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10, fill: axisColor }} tickLine={false} axisLine={false} domain={[0, dataMax => Math.max(dataMax, 200)]} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                <YAxis type="category" dataKey="agent" width={150} tick={{ fontSize: 11, fill: textMuted }} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomTooltip isDark={isDark} />} />
                <Bar dataKey="queries" name="Queries" fill="#F87217" radius={[0, 6, 6, 0]} barSize={14} />
              </BarChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px 0', fontSize: 10, color: textMuted, fontWeight: 500 }}>
              <span>Y-Axis: Agents</span>
              <span>X-Axis: Queries</span>
            </div>
          </ChartCard>
        </div>

        {/* Usage by Category Pie */}
        <div ref={categoryPieRef}>
          <ChartCard
            title="Usage by Category"
            subtitle="% share of total queries"
            isDark={isDark}
            action={<DownloadBtn loading={dlKey === 'category'} isDark={isDark} onClick={() => dl(categoryPieRef, 'category', 'usage-by-category')} />}
          >
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={agentUsageByCategoryData}
                  cx="50%" cy="50%"
                  outerRadius={80} paddingAngle={2} dataKey="value"
                  label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
                >
                  {agentUsageByCategoryData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} stroke="transparent" />
                  ))}
                </Pie>
                <Legend
                  iconType="circle" iconSize={8}
                  formatter={(v) => <span style={{ fontSize: 10, color: isDark ? '#94a3b8' : '#64748b' }}>{v}</span>}
                />
                <Tooltip
                  formatter={(value, name) => [`${value}%`, name]}
                  contentStyle={{ background: isDark ? '#0f1729' : '#fff', border: `1px solid ${isDark ? '#1e2d52' : '#e2e8f0'}`, borderRadius: 12, fontSize: 12, color: isDark ? '#c9d1d9' : '#374151' }}
                  itemStyle={{ color: isDark ? '#c9d1d9' : '#374151' }}
                  labelStyle={{ color: isDark ? '#f0f6fc' : '#0f172a', fontWeight: 600 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>

      {/* ── Row 3: Credits Stacked Area ──────────────────────────────── */}
      <div ref={creditsRef}>
        <ChartCard
          title="Credits Issued vs Consumed"
          subtitle="Last 12 months · monthly totals"
          isDark={isDark}
          action={<DownloadBtn loading={dlKey === 'credits'} isDark={isDark} onClick={() => dl(creditsRef, 'credits', 'credits-issued-consumed')} />}
        >
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={creditsData} margin={{ top: 4, right: 4, bottom: 0, left: -10 }}>
              <defs>
                <linearGradient id="issuedGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="10%" stopColor="#8b5cf6" stopOpacity={0.3} />
                  <stop offset="90%" stopColor="#8b5cf6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="consumedGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="10%" stopColor="#F87217" stopOpacity={0.3} />
                  <stop offset="90%" stopColor="#F87217" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke={gridColor} vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 10, fill: axisColor }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 10, fill: axisColor }} tickLine={false} axisLine={false} domain={[0, dataMax => Math.max(dataMax, 200)]} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
              <Tooltip
                contentStyle={{ background: isDark ? '#0f1729' : '#fff', border: `1px solid ${isDark ? '#1e2d52' : '#e2e8f0'}`, borderRadius: 12, fontSize: 12, color: isDark ? '#c9d1d9' : '#374151' }}
                itemStyle={{ color: isDark ? '#c9d1d9' : '#374151' }}
                formatter={(v) => v.toLocaleString()}
              />
              <Legend
                iconType="circle" iconSize={8}
                formatter={(v) => <span style={{ fontSize: 11, color: isDark ? '#94a3b8' : '#64748b' }}>{v}</span>}
              />
              <Area type="monotone" dataKey="issued" name="Issued" stroke="#8b5cf6" strokeWidth={2} fill="url(#issuedGrad)" dot={false} />
              <Area type="monotone" dataKey="consumed" name="Consumed" stroke="#F87217" strokeWidth={2} fill="url(#consumedGrad)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px 0', fontSize: 10, color: textMuted, fontWeight: 500 }}>
            <span>Y-Axis: Credits</span>
            <span>X-Axis: Month</span>
          </div>
        </ChartCard>
      </div>

    </div>
  );
};

export default SuperAdminDashboard;